/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { toMoney } from "shared/utils";
import { Warning } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/common";
import { PaystubDocumentPaystub } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/paystub";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { SimpleTable } from "../components/SimpleTable";
import { EmployeeInformationSlice } from "./EmployeeInformationSlice";
import { EmployerInformationSlice } from "./EmployerInformationSlice";
import { PaystubDetailsSlice } from "./PaystubDetailsSlice";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { WarningRecommendation } from "../../WarningRecommendation";
import { Show } from "shared/components";

export const ExpandedTableRow = ({
  rowData
}: {
  rowData: PaystubDocumentPaystub | Warning;
}) => {
  const isWarningEntity = "doc_info" in rowData;
  const formAnalysis = isWarningEntity
    ? rowData?.form_analysis
    : rowData?.doc_analysis?.form_analysis;
  const docScore = formAnalysis[0]?.form_authenticity?.score ?? 0,
    reasonCodes = formAnalysis[0]?.form_authenticity?.reason_codes ?? [];

  const status = parseDocStatus(docScore);
  const rejectionReason = isWarningEntity
    ? (rowData?.doc_info.rejection_reason ?? reasonCodes[0]?.description)
    : reasonCodes[0]?.description;
  const hasAsset = Boolean(rowData?.asset?.asset);

  return (
    <>
      <WarningRecommendation
        status={status}
        rejectionReason={rejectionReason}
      />

      <Show if={hasAsset}>
        <div className="h-[1px] w-full bg-alpha-40 my-3" />
        <DocumentCard
          score={docScore}
          asset={rowData?.asset?.asset}
          reasonCodes={reasonCodes}
          ignorePrint
        />
      </Show>

      {isWarningEntity ? null : (
        <>
          <PaystubDetailsSlice paystub={rowData} />
          <EmployerInformationSlice paystub={rowData} />
          <EmployeeInformationSlice paystub={rowData} />

          {/* Pay distribution table */}
          <SimpleTable
            data={rowData?.pay_distribution}
            columns={[
              {
                label: "Bank Name",
                render: (d) => String(d.bank_name) || "-"
              },
              {
                label: "Current pay",
                render: (d) => toMoney((d.current_pay?.cents || 0) / 100)
              },
              {
                label: "Account",
                render: (d) => String(d.account_number) || "-"
              },
              {
                label: "Type",
                render: (d) => d.bank_account_type || "-"
              },
              {
                label: "Description",
                render: (d) => d.description || "-"
              }
            ]}
          />

          <div className="h-px w-full bg-alpha-100" />

          {/* Earnings table */}
          {/* Earning | Current pay | YTD pay | Cur. hrs | Description  */}
          <SimpleTable
            data={rowData?.earnings.totals}
            columns={[
              {
                label: "Earning",
                render: (e) => e.description || "-"
              },
              {
                label: "Current pay",
                render: (e) =>
                  !e.current_pay ? "-" : toMoney(e.current_pay.cents / 100)
              },
              {
                label: "YTD pay",
                render: (e) =>
                  !e.ytd_pay ? "-" : toMoney(e.ytd_pay.cents / 100)
              },
              {
                label: "Current hours",
                render: (e) => e.current_hours || "-"
              },
              {
                label: "Description",
                render: (e) => e.canonical_description || "-"
              }
            ]}
          />

          {/* Deduction table  */}
          {/* Deduction | Current pay */}
          <SimpleTable
            data={rowData?.deductions.totals}
            columns={[
              {
                label: "Deduction",
                render: (d) => d.description || "-",
                className: "w-1/5"
              },
              {
                label: "Current pay",
                render: (d) =>
                  !d.current_pay ? "-" : toMoney(d.current_pay.cents / 100)
              }
            ]}
          />
        </>
      )}
    </>
  );
};
